import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import { Router } from 'react-router-dom';
import { renderRoutes } from 'react-router-config';
import { PersistGate } from 'redux-persist/integration/react';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import ScrollRestoration from 'react-scroll-restoration';
import { getAnalytics } from 'firebase/analytics';
import { onMessage } from 'firebase/messaging';
import { useClearCache } from 'react-clear-cache';
import { toast, ToastContainer, Bounce } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import moment from 'moment-timezone';
import { CircularProgress } from '@material-ui/core';

import ApolloWithOAuth from '@services/ApolloWithOAuth';

import { store, persistor } from '@store';
import { routes } from '@router';
import mainTheme from '@styled/themes/mainTheme';
import GlobalStyle from '@styled/globalStyle';
import history from '@utils/history';
import EnglishTranslation from '@utils/location/en';
import FranceTranslation from '@utils/location/fr';
import { getLanguage, gApp, requestPermission, messaging } from '@utils/config';
import { isPre, isProd } from '@config/getEnviroment';
import { MenuProvider } from '@layouts/common/Menu';
import Notifications from '@components/Notifications';
import ErrorBoundary from '@components/ErrorBoundary';
import Message from '@components/Message';
import LoadingHooked from '@components/LoadingHooked';
import GoTop from '@components/GoTop';

moment.tz.setDefault('Europe/Paris');
i18n.use(initReactI18next).init({
  resources: {
    en: {
      ...EnglishTranslation,
    },
    fr: {
      ...FranceTranslation,
    },
  },
  lng: getLanguage(),
  fallbackLng: 'en',
  interpolation: {
    escapeValue: false,
  },
});
const isPrePro = isPre();
const isProduction = isProd();
if (isPrePro || isProduction) {
  getAnalytics(gApp);
}

const App = () => {
  const { isLatestVersion, emptyCacheStorage } = useClearCache();

  useEffect(() => {
    if (!isProduction) {
      requestPermission();
    }
  }, []);

  if (!isProduction) {
    onMessage(messaging, (payload) => {
      toast(<Message notification={payload.notification} />);
    });
  }

  if (!isLatestVersion) {
    emptyCacheStorage()
      .then(() => window.location.reload())
      .catch(() => window.location.reload());

    return <CircularProgress size={40} />;
  }

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <ApolloWithOAuth>
            <ThemeProvider theme={mainTheme}>
              <LoadingHooked />
              <GoTop />
              <GlobalStyle />
              <Notifications />
              <ToastContainer
                position="top-center"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
                transition={Bounce}
              />
              <MenuProvider>
                <Router history={history}>
                  <ScrollRestoration />
                  <React.Suspense fallback={<p />}>{renderRoutes(routes)}</React.Suspense>
                </Router>
              </MenuProvider>
            </ThemeProvider>
          </ApolloWithOAuth>
        </PersistGate>
      </Provider>
    </ErrorBoundary>
  );
};

export default App;
